/**
 * Generating a batch of teaching puzzles from a slide deck and an admin's
 * steering text, for the admin generation page. Generated puzzles are saved
 * unpublished in the course's puzzles; the batch doc records how each was
 * made and what its audit found.
 */
import {z} from 'zod';

/** The Cloud Function that generates or regenerates puzzles for an admin. */
export const GENERATE_TEACHING_PUZZLES_FUNCTION = 'generateteachingpuzzles';

/** Firestore subcollection (under `course/{courseId}`) of generation batches. */
export const PUZZLE_GENERATION_BATCHES_COLLECTION = 'puzzleGenerationBatches';

/** The most puzzles one request may generate. */
export const MAX_GENERATED_PUZZLES = 10;

/** The longest steering or tuning text, in characters. */
export const MAX_PUZZLE_STEERING_CHARS = 4000;

/**
 * How long the client waits for {@link GENERATE_TEACHING_PUZZLES_FUNCTION};
 * the function's own timeout is the same.
 */
export const GENERATE_TEACHING_PUZZLES_TIMEOUT_MS = 20 * 60 * 1000;

/** A slide deck: `slides/{courseId}/decks/{deckId}`. */
export const PuzzleGenerationDeckSchema = z.object({
  /** The course whose slide decks hold it. */
  courseId: z.string().trim().min(1),
  /** The deck's id. */
  deckId: z.string().trim().min(1),
});

/** A slide deck reference. */
export type PuzzleGenerationDeck = z.infer<typeof PuzzleGenerationDeckSchema>;

/** Request for {@link GENERATE_TEACHING_PUZZLES_FUNCTION}: a new batch. */
export const GeneratePuzzlesRequestSchema = z.object({
  kind: z.literal('generate'),
  /** The course the puzzles are for. */
  courseId: z.string().trim().min(1),
  /** How many puzzles to write. */
  count: z.number().int().min(1).max(MAX_GENERATED_PUZZLES),
  /** What to generate from, in the admin's words. May be empty. */
  steering: z.string().max(MAX_PUZZLE_STEERING_CHARS),
  /** The deck puzzles open on. Null: no slides, conceptual puzzles. */
  deck: PuzzleGenerationDeckSchema.nullable(),
  /** The section week, for tags and ids. */
  sectionWeek: z.number().int().positive(),
  /** Whether to publish each puzzle as it's saved. Omitted: unpublished. */
  publish: z.boolean().optional(),
});

/** Request to rewrite some of a batch's puzzles with tuning instructions. */
export const RegeneratePuzzlesRequestSchema = z.object({
  kind: z.literal('regenerate'),
  /** The course the puzzles are in. */
  courseId: z.string().trim().min(1),
  /** The batch the puzzles came from. */
  batchId: z.string().trim().min(1),
  /** The puzzles to rewrite, as they're saved now (edits included). */
  puzzleIds: z
    .array(z.string().trim().min(1))
    .min(1)
    .max(MAX_GENERATED_PUZZLES),
  /** What to change, in the admin's words. */
  instructions: z.string().trim().min(1).max(MAX_PUZZLE_STEERING_CHARS),
});

/** Request for {@link GENERATE_TEACHING_PUZZLES_FUNCTION}. */
export const GenerateTeachingPuzzlesRequestSchema = z.discriminatedUnion(
  'kind',
  [GeneratePuzzlesRequestSchema, RegeneratePuzzlesRequestSchema],
);

/** Request for {@link GENERATE_TEACHING_PUZZLES_FUNCTION}. */
export type GenerateTeachingPuzzlesRequest = z.infer<
  typeof GenerateTeachingPuzzlesRequestSchema
>;

/** Response from {@link GENERATE_TEACHING_PUZZLES_FUNCTION}. */
export interface GenerateTeachingPuzzlesResponse {
  /** The batch written or updated. */
  batchId: string;
  /** Puzzles saved by this request. */
  puzzleIds: string[];
  /** Items that couldn't be written, with why. */
  failures: string[];
}

/** One problem the auditor found. */
export const PuzzleAuditProblemSchema = z.object({
  /** The draft field it's about, e.g. `openingLine` or `chain`. */
  field: z.string(),
  /** What's wrong. */
  problem: z.string(),
});

/** One problem the auditor found. */
export type PuzzleAuditProblem = z.infer<typeof PuzzleAuditProblemSchema>;

/** What the auditor said about the final draft. */
export const PuzzleAuditSchema = z.object({
  /** Whether the final draft passed. */
  passed: z.boolean(),
  /** Problems left in the final draft; empty when it passed. */
  problems: z.array(PuzzleAuditProblemSchema),
  /** How many rewrites the audit asked for before this verdict. */
  revisions: z.number().int().min(0),
});

/** What the auditor said about the final draft. */
export type PuzzleAudit = z.infer<typeof PuzzleAuditSchema>;

/** One puzzle of a batch. */
export const PuzzleGenerationItemSchema = z.object({
  /** Its id under `course/{courseId}/simTeachPuzzles`. */
  puzzleId: z.string(),
  /**
   * The misconception's reasoning chain, from what's on screen to the
   * stuck student's opening line, one step per entry.
   */
  chain: z.array(z.string()),
  /** The final audit. */
  audit: PuzzleAuditSchema,
  /** Tuning instructions applied to it, oldest first. Omitted: none. */
  tunings: z.array(z.string()).optional(),
  /** Epoch ms it was last written. */
  updatedAtMs: z.number(),
});

/** One puzzle of a batch. */
export type PuzzleGenerationItem = z.infer<typeof PuzzleGenerationItemSchema>;

/** A generation batch: `course/{courseId}/puzzleGenerationBatches/{id}`. */
export const PuzzleGenerationBatchSchema = z.object({
  /** Who asked for it. */
  createdBy: z.string(),
  /** Epoch ms it was asked for. */
  createdAtMs: z.number(),
  /** How many puzzles were asked for. */
  count: z.number().int(),
  /** The steering text. */
  steering: z.string(),
  /** The deck, or null for conceptual puzzles. */
  deck: PuzzleGenerationDeckSchema.nullable(),
  /** The section week. */
  sectionWeek: z.number().int(),
  /** `generating` while a request runs on it, then `done` or `failed`. */
  status: z.enum(['generating', 'done', 'failed']),
  /** What's running now, for the page. Omitted: nothing. */
  progress: z.string().optional(),
  /** Why the last request failed. Omitted: it didn't. */
  error: z.string().optional(),
  /** Its puzzles, in order. */
  items: z.array(PuzzleGenerationItemSchema),
});

/** A generation batch. */
export type PuzzleGenerationBatch = z.infer<typeof PuzzleGenerationBatchSchema>;

/**
 * Path to a generation batch.
 *
 * @param params.courseId - The course.
 * @param params.batchId - The batch.
 * @return The document path.
 * @throws Never.
 */
export function puzzleGenerationBatchDocPath({
  courseId,
  batchId,
}: {
  courseId: string;
  batchId: string;
}): string {
  return `course/${courseId}/${PUZZLE_GENERATION_BATCHES_COLLECTION}/${batchId}`;
}

/**
 * The id of a batch's puzzle: `gen-w<week>-<batch>-<n>`, 1-based.
 *
 * @param params.sectionWeek - The batch's section week.
 * @param params.batchId - The batch; lowercase letters and digits.
 * @param params.index - The puzzle's 0-based position in the batch.
 * @return The puzzle id.
 * @throws Never.
 */
export function generatedPuzzleId({
  sectionWeek,
  batchId,
  index,
}: {
  sectionWeek: number;
  batchId: string;
  index: number;
}): string {
  return `gen-w${String(sectionWeek)}-${batchId}-${String(index + 1)}`;
}
